import { useState, useEffect, useCallback, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Moment from "moment";
import AuthContext from "../../../auth/AuthContext";
import SupportService from "../../../services/SupportService";
import AgentService from "../../../services/AgentService";
import Attachment from "../support/Attachment";
import ViewComment from "../support/VIewComment";
import Comment from "../support/Comment";
import { FaArrowLeft, FaSyncAlt } from "react-icons/fa";
import ReassignModal from "./ReassignModal";
import AlertService from "../../../services/AlertService";

const AgentView = ({ ticket, clearSelection, loadTickets, statusHandler }) => {
    const [comments, setComments] = useState([]);
    const [agents, setAgents] = useState([]);
    const authContext = useContext(AuthContext);
    const [viewReassign, setViewReassign] = useState(false);
    const [assignedTo, setAssignedTo] = useState(ticket.assignedTo);

const loadComments = useCallback(async (signal) => { 
    if (ticket) {
      try {
        const data = await SupportService.GetComments(
          ticket.id,
          authContext,
          signal 
        );

        if (signal?.aborted) return; 

        if (data.isSuccess) {
          setComments(data.data);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    }
  }, [ticket, authContext]);

const loadAgents = useCallback(async (signal) => { 
    try {
      const data = await AgentService.GetAgentList(
        1,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setAgents(data.data);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadComments(controller.signal); 
    return () => controller.abort(); 
  }, [loadComments]);

useEffect(() => {
    const controller = new AbortController();
    loadAgents(controller.signal);
    return () => controller.abort(); 
  }, [loadAgents]);

    const onCommentSaveHandler = async () => {
        await loadComments();
    };

    const markAsInProgress = async (e) => {
        e.preventDefault();
        await SupportService.SetStatus(ticket.id, 2, authContext);
        ticket.status = 2;
        ticket.statusDesc = "In-Progress";
        statusHandler(ticket.id, ticket.status, ticket.statusDesc);
    };
   const markAsClosed = async (e) => {
        e.preventDefault();
        await SupportService.SetStatus(ticket.id, 3, authContext);
        ticket.status = 3;
        ticket.statusDesc = "Closed";
        statusHandler(ticket.id, ticket.status, ticket.statusDesc);
    };
const ReAssignTicket = async (e) => {
    e.preventDefault();
    try {
        const res = await SupportService.Reassign(ticket.id, assignedTo, authContext);
        if (res.isSuccess) {
            AlertService.success("Ticket reassigned successfully");
            setViewReassign(false);
            clearSelection();
            loadTickets();
        } else {
           AlertService.error("Reassign failed");
        }
    } catch {
   AlertService.error("Reassign error");
    }
};

    return (
        <Container fluid className="mb-5 mt-3 mt-md-0">
            <div className="d-flex justify-content-end align-items-center gap-3">
                <div className="d-flex align-items-center gap-3 mb-3  mb-md-0">
                    {ticket.status === 1 && (
                        <button
                            type="button"
                            className="btn btn-warning"
                            onClick={markAsInProgress}
                        >
                            Mark as In-Progress
                        </button>
                    )}
                    {ticket.status === 2 && (
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={markAsClosed}
                        >
                            Close Ticket
                        </button>
                    )}
                    <button
                        className="btn btn-primary btn-sm px-4 py-2 d-flex align-items-center gap-2"
                        onClick={() => setViewReassign(true)}
                    >
                        <FaSyncAlt size={14} />
                        Reassign
                    </button>
                    <button
                        className="btn btn-outline btn-sm d-flex align-items-center gap-2 px-4 py-2"
                        onClick={clearSelection}
                    >
                        <FaArrowLeft />
                        Back
                    </button>
                </div>
            </div>

            <Row className="mb-3">
                <Col xs={6}>
                    <div className="mb-1 support-label">Requested By</div>
                    <div>{ticket.requester}</div>
                </Col>
                <Col xs={6}>
                    <div className="mb-1 support-label">Ticket</div>
                    <div>{ticket.ticketId}</div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={6}>
                    <div className="mb-1 support-label">Status</div>
                    <div>
                        {ticket.statusDesc}
                        {ticket.status === 3 && ticket.closedDate && (
                            <>
                                , Date:{" "}
                                {Moment(ticket.closedDate).format("DD/MM/YYYY hh:mm A")}
                            </>
                        )}
                    </div>
                </Col>
                <Col xs={6}>
                    <div className="mb-1 support-label">Category</div>
                    <div>{ticket.categoryDesc}</div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="mb-1 support-label">Course Name</div>
                    <div>{ticket.courseName}</div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="mb-1 support-label">Subject</div>
                    <div>{ticket.subject}</div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="mb-1 support-label">Description</div>
                    <div
                        style={{
                            fontSize: "0.95rem",
                            whiteSpace: "pre-line",
                        }}
                    >
                        {ticket.description}
                    </div>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="mb-1 support-label">Attachments</div>
                    <div className="d-flex flex-wrap gap-3">
                        {ticket.attachments?.map((att, i) => (
                            <Attachment key={i} no={i + 1} attachment={att} />
                        ))}
                    </div>
                </Col>
            </Row>
            {comments.length > 0 && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="mb-1 support-label">Comments</div>
                        {comments.map((com, i) => (
                            <ViewComment key={i} comment={com} />
                        ))}
                    </Col>
                </Row>
            )}
            {ticket.status !== 3 && (
                <Row>
                    <Col xs={12}>
                        <Comment
                            ticketId={ticket.id}
                            submitHandler={onCommentSaveHandler}
                            cancelHandler={clearSelection}
                        />
                    </Col>
                </Row>
            )}
            <ReassignModal
                show={viewReassign}
                onHide={() => setViewReassign(false)}
                agents={agents}
                assignedTo={assignedTo}
                setAssignedTo={setAssignedTo}
                onSubmit={ReAssignTicket}
                onCancel={() => setViewReassign(false)}
            />
        </Container>
    );
};

export default AgentView;
